<template>
    <div class="contact-list-container">
        <table class="contact-table">
            <thead>
                <tr>
                    <th />
                    <th class="item-contact">
                        {{ $t('contactSelection.table.contact') }}
                        <ReglePastille cle="demandeIntervention.contactsSuivi" />
                    </th>
                    <th>
                        {{ $t('contactSelection.table.mail') }}
                    </th>
                    <th class="buttons-container" />
                </tr>
            </thead>
            <tbody>
                <tr v-on:click="toggleSelect(contact)" v-for="contact in contacts" :key="`${contact.email}`">
                    <td class="select">
                        <div class="select-border checkbox">
                            <CheckBox :checked="selectedContacts.map(c => c.email).includes(contact.email)" label=""/>
                        </div>
                    </td>
                    <td class="item-contact">
                        <div class="item-name">
                            <span>
                                {{ `${contact.firstname} ${contact.lastname.toUpperCase()}` }}
                            </span>
                        </div>
                    </td>
                    <td>
                        <div class="item-mail">
                            <span>{{ contact.email }}</span>
                        </div>
                    </td>
                    <td class="buttons-container">
                        <div>
                            <img
                                @click.stop="openEdit(contact)"
                                :src="getEditIcon"
                                class="icon icon-edit"
                                alt="Edit Contact"
                            />
                            <img
                                v-if="contacts.length > 1"
                                @click.stop="removeContact(contact)"
                                :src="getDeleteIcon"
                                class="icon"
                                alt="Remove Contact"
                            />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <bordered-icon-button
            :isDisabled="contacts.length > 4"
            class="add-button"
            :onClick="openAdd"
            :title="$t('contactSelection.table.add')"
            :imgUrl="loadAddIcon"
        />
    </div>
    <ContactForm
        v-if="contactModal"
        @confirm="confirmContact"
        :contact="contactToEdit"
        @close="closeModal"
        :isPhone="false"
    />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { INTERVENTION } from '@/modules/Intervention/store/mutation-types';
import ContactForm from './ContactForm.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { DemandeInterventionContact } from '@/models/DemandeInterventionContact';
import { ContactFormState } from '@/models/ContactFormState';
import { InterventionModuleAction } from '@/modules/Intervention/store/actions-types';
import CheckBox from '@/components/ui-components/CheckBox.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface ContactListData {
    contactModal: boolean;
    contactToEdit?: DemandeInterventionContact;
}

export default defineComponent({
    data(): ContactListData {
        return {
            contactModal: false,
            contactToEdit: undefined,
        };
    },
    mounted() {
        if (this.contacts.length === 0) {
            this.$store.dispatch(InterventionModuleAction.PREFILL_CONNECTED_CONTACT_SUIVI);
        }
    },
    components: {
        CheckBox,
        ContactForm,
        BorderedIconButton,
        ReglePastille,
    },
    computed: {
        selectedContacts(): DemandeInterventionContact[] {
            return this.$store.getters.getInterventionContactsValue;
        },
        getEditIcon() {
            return require('@/assets/img/edit.svg');
        },
        getDeleteIcon() {
            return require('@/assets/img/delete.svg');
        },
        loadAddIcon() {
            return require('@/assets/img/add-button.svg');
        },
        contacts(): DemandeInterventionContact[] {
            return this.$store.getters.getInterventionContactsListValue;
        },
    },
    methods: {
        closeModal() {
            this.contactToEdit = undefined;
            this.contactModal = false;
        },
        openAdd() {
            if (this.contacts.length < 5) this.contactModal = true;
        },
        openEdit(contact: DemandeInterventionContact) {
            this.contactToEdit = contact;
            this.contactModal = true;
        },
        toggleSelect(contact: ContactFormState) {
            if (this.selectedContacts.map(c => c.email).includes(contact.email)) {
                if (this.selectedContacts.length > 1) {
                    this.$store.commit(
                        INTERVENTION.SET_CONTACT,
                        this.selectedContacts.filter(c => c.email !== contact.email)
                    );
                }
            } else {
                this.$store.commit(INTERVENTION.SET_CONTACT, [...this.selectedContacts, contact]);
            }
        },
        confirmContact(contact: ContactFormState) {
            if (this.contactToEdit) {
                this.removeContact(this.contactToEdit);
            }
            this.addContact(contact);
            this.closeModal();
        },
        addContact(contact: ContactFormState) {
            this.$store.commit(INTERVENTION.ADD_CONTACT, contact);
            this.closeModal();
        },
        removeContact(contact: DemandeInterventionContact) {
            this.$store.commit(INTERVENTION.REMOVE_CONTACT, contact);
        },
    },
});
</script>

<style scoped>
.contact-list-container {
    display: flex;
    flex-direction: column;
}
.item-info {
    color: var(--medium-dark-grey);
}
.item-contact {
    width: 30%;
}
.icon {
    height: 24px;
    width: 24px;
    margin: 0;
    cursor: pointer;
}
.icon-edit {
    margin-right: 16px;
}
.buttons-container div {
    display: flex;
    justify-content: flex-end;
}
.add-button {
    margin-top: 8px;
    align-self: flex-end;
}

.contact-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-right: 1px solid var(--medium-dark-grey);
    border-left: 1px solid var(--medium-dark-grey);
    border-bottom: 1px solid var(--medium-dark-grey);
    border-radius: 3px;
    text-align: left;
}

.contact-table thead {
    position: sticky;
    top: 0;
    font-size: 14px;
    color: var(--dark-grey);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
}

.contact-table thead th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid var(--medium-dark-grey);
    border-top: 1px solid var(--medium-dark-grey);
    background-color: white;
    height: 55px;
}

.contact-table thead th:first-child {
    border-top-left-radius: 3px;
    width: 100px;
}

.contact-table thead th:last-child {
    border-top-right-radius: 3px;
}

.contact-table tr td {
    border-bottom: 1px solid var(--medium-dark-grey);
    height: 55px;
}

.contact-table tbody tr:last-child td {
    border: none;
}

.contact-table thead tr:first-child {
    border-top: none;
}

.contact-table tr td {
    box-sizing: border-box;
    padding: 0 5px;
}
:deep(.checkmark){
    left: 15px;
    top: -10px;
}
</style>
